import { useFeatureAccess } from '@hooks/useFeatureAccess';
import type { AnalysisConfig } from '@views/picks/constants';
import type { ReactElement } from 'react';
import { Section } from './Section';

interface MLConfigSectionProps {
  cfg: AnalysisConfig;
  isExpanded: boolean;
  onToggleExpand: (sectionKey: string) => void;
  setConfig: React.Dispatch<React.SetStateAction<AnalysisConfig>>;
}

const MODE_OPTIONS = [
  { value: 'llm' as const, label: 'LLM', desc: 'AI generates picks and groups parlays' },
  {
    value: 'shadow' as const,
    label: 'Shadow',
    desc: 'LLM picks + silent ML scoring logged for comparison',
  },
  { value: 'hybrid' as const, label: 'Hybrid', desc: 'ML selects legs, LLM writes narratives' },
  {
    value: 'optimized' as const,
    label: 'Optimized',
    desc: 'LLM picks legs, smart algorithm groups parlays',
  },
  {
    value: 'ml' as const,
    label: 'Full ML',
    desc: 'ML handles everything — scoring, selection, grouping',
  },
] as const;

export function MLConfigSection({
  cfg,
  isExpanded,
  onToggleExpand,
  setConfig,
}: MLConfigSectionProps): ReactElement | null {
  const { isHidden } = useFeatureAccess();

  if (isHidden('ml_generation_mode')) return null;

  const mode = cfg.ml?.generationMode ?? 'shadow';

  return (
    <Section
      title="Generation Mode"
      sectionKey="ml"
      isExpanded={isExpanded}
      onToggleExpand={onToggleExpand}
      enabledCount={0}
      totalCount={0}
      showCount={false}
    >
      <div className="space-y-3">
        <div className="space-y-1.5">
          <span className="text-sm text-theme-text-secondary">Pick Generation Engine</span>
          <div className="grid grid-cols-2 gap-1 bg-theme-surface rounded-lg p-1">
            {MODE_OPTIONS.map((opt) => {
              const isActive = mode === opt.value;
              return (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() =>
                    setConfig((prev) => ({
                      ...prev,
                      ml: { ...prev.ml, generationMode: opt.value },
                    }))
                  }
                  className={`px-2 py-1.5 rounded-md text-xs font-medium transition relative ${
                    isActive
                      ? 'bg-purple-600 text-white'
                      : 'text-theme-text-secondary hover:text-gray-200 hover:bg-theme-surface-hover'
                  }`}
                  title={opt.desc}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-theme-text-tertiary">
            {MODE_OPTIONS.find((o) => o.value === mode)?.desc}
          </p>
        </div>
      </div>
    </Section>
  );
}
